import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@safe-minds-ai/ui';
import { fetchUserDetail, getAdminChildDisplayName, updateUser, UserDetail } from '../api';
import { useToast } from '../toast';

export default function UserDetailRoute(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeletedChildren, setShowDeletedChildren] = useState(false);
  const [nickname, setNickname] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [age, setAge] = useState('');
  const [grade, setGrade] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const backToParentId = (location.state as { fromParentId?: string })?.fromParentId;

  const loadUser = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await fetchUserDetail(id);
      setUser(data);
      setError(null);
      if (data.parentProfile) {
        setNickname('');
        setFirstName(data.parentProfile.firstName);
        setLastName(data.parentProfile.lastName);
      }
      if (data.childProfile) {
        setNickname(data.childProfile.nickname);
        setFirstName(data.childProfile.firstName ?? '');
        setLastName(data.childProfile.lastName ?? '');
        setAge(String(data.childProfile.age));
        setGrade(data.childProfile.grade == null ? '' : String(data.childProfile.grade));
      }
      if (data.role === 'ADMIN') {
        setNickname('');
        setFirstName('');
        setLastName('');
      }
    } catch (err) {
      setError('Unable to load user.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [id]);

  const filteredChildren = useMemo(() => {
    if (!user?.children) return [];
    return showDeletedChildren ? user.children : user.children.filter((child) => !child.isDeleted);
  }, [user?.children, showDeletedChildren]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      if (user.role === 'PARENT') {
        await updateUser(user.id, {
          parentProfile: { firstName: firstName.trim(), lastName: lastName.trim() },
        });
      } else if (user.role === 'CHILD') {
        await updateUser(user.id, {
          childProfile: {
            nickname: nickname.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            age: Number(age || 0),
            grade: grade ? Number(grade) : undefined,
          },
        });
      }
      await loadUser();
      toast.push({ title: 'Saved', description: 'Profile updated.', variant: 'success' });
    } catch (err: any) {
      toast.push({ title: 'Error', description: err.response?.data?.message || 'Failed to update profile.', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDelete = async (disable: boolean) => {
    if (!user) return;
    try {
      await updateUser(user.id, disable ? { disable: true } : { enable: true });
      await loadUser();
      toast.push({
        title: disable ? 'User deleted' : 'User restored',
        description: disable ? 'Account has been deleted.' : 'Account restored.',
        variant: 'success',
      });
    } catch (err: any) {
      toast.push({ title: 'Error', description: err.response?.data?.message || 'Failed to update status.', variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl bg-white/80 p-8 text-slate-500 dark:bg-slate-900/80 dark:text-slate-300">Loading…</div>;
  }

  if (error || !user) {
    return <div className="rounded-2xl bg-white/80 p-8 text-rose-600 dark:bg-slate-900/80">User not found.</div>;
  }

  const userTitle = user.role === 'CHILD' ? getAdminChildDisplayName(user.childProfile ?? user) : user.name || user.email;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300">Account</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">{userTitle}</h1>
            <Badge className="rounded-full bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
              {user.role}
            </Badge>
            {user.isDeleted ? <Badge className="rounded-full bg-rose-600 text-white">Deleted</Badge> : null}
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">ID: {user.id}</p>
        </div>
        <div className="flex items-center gap-2">
          {user.role === 'CHILD' && (backToParentId || user.parentSummary?.id) ? (
            <Button
              variant="ghost"
              className="rounded-full"
              onClick={() => navigate(`/users/${backToParentId ?? user.parentSummary?.id}`)}
            >
              Back to parent
            </Button>
          ) : null}
          <Button variant="ghost" className="rounded-full" onClick={() => navigate('/users')}>
            Back to users
          </Button>
        </div>
      </div>

      <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/80 dark:bg-slate-900/80">
        <CardHeader>
          <CardTitle>Account details</CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Created {new Date(user.createdAt).toLocaleString()}
            {user.deletedAt ? ` · Deleted ${new Date(user.deletedAt).toLocaleString()}` : ''}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Profile</p>
                    <p className="text-xs text-slate-500 dark:text-slate-300">Manage identity details.</p>
                  </div>
                  {user.role !== 'ADMIN' ? (
                    <Button className="rounded-full" disabled={saving || (user.role === 'CHILD' && !nickname.trim())} onClick={handleSaveProfile}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                  ) : null}
                </div>
                {user.role === 'ADMIN' ? (
                  <div className="grid gap-2">
                    <Label>Email</Label>
                    <Input value={user.email} disabled />
                  </div>
                ) : (
                  <>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Nickname</Label>
                        <Input value={nickname} onChange={(e) => setNickname(e.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label>First name</Label>
                        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label>Last name</Label>
                        <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                      </div>
                    </div>
                    {user.role === 'CHILD' ? (
                      <div className="grid gap-2 md:grid-cols-2">
                        <div className="grid gap-2">
                          <Label>Age</Label>
                          <Input value={age} onChange={(e) => setAge(e.target.value)} />
                        </div>
                        <div className="grid gap-2">
                          <Label>Grade</Label>
                          <Input value={grade} onChange={(e) => setGrade(e.target.value)} />
                        </div>
                      </div>
                    ) : null}
                    <div className="grid gap-2">
                      <Label>Email</Label>
                      <Input value={user.email ?? ''} disabled />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200/60 bg-white/90 p-6 dark:border-slate-700/40 dark:bg-slate-900/80 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Account status</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">Security and lifecycle controls.</p>
                </div>
                <div className="grid gap-3 text-sm text-slate-600 dark:text-slate-300">
                  <div className="flex items-center justify-between">
                    <span>Access</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {user.isDeleted ? 'Deleted' : user.role === 'CHILD' ? 'Active' : user.approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Role</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{user.role}</span>
                  </div>
                  {user.role === 'CHILD' && user.parentSummary?.name ? (
                    <div className="flex items-center justify-between">
                      <span>Parent</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {user.parentSummary.name}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {user.isDeleted ? (
                    <Button className="rounded-full" onClick={() => handleToggleDelete(false)}>
                      Restore user
                    </Button>
                  ) : (
                    <Button className="rounded-full bg-rose-600 text-white hover:bg-rose-500" onClick={() => setDeleteDialogOpen(true)}>
                      Delete user
                    </Button>
                  )}
                </div>
                {user.role === 'PARENT' && !user.isDeleted ? (
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    Deleting a parent also deletes all active children linked to them.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Relationships</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-300">Linked parents and children.</p>
                </div>
                {user.role === 'PARENT' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full"
                    onClick={() => setShowDeletedChildren((prev) => !prev)}
                  >
                    {showDeletedChildren ? 'Hide deleted' : 'Show deleted'}
                  </Button>
                ) : null}
              </div>
              {user.role === 'PARENT' ? (
                filteredChildren.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/50 p-6 text-center text-sm text-slate-500 dark:text-slate-300">
                    This parent has no children.
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {filteredChildren.map((child) => (
                      <div
                        key={child.id}
                        className="flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4"
                      >
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-slate-100">{child.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-300">{child.email ?? child.id}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {child.isDeleted ? <Badge className="rounded-full bg-rose-600 text-white">Deleted</Badge> : null}
                          <Button
                            size="sm"
                            className="rounded-full"
                            onClick={() => navigate(`/users/${child.id}`, { state: { fromParentId: user.id } })}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : null}
              {user.role === 'CHILD' ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4 space-y-2">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Linked parent</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    {user.parentSummary?.name || user.parentSummary?.email || 'No parent linked'}
                  </p>
                </div>
              ) : null}
              {user.role === 'ADMIN' ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4 text-sm text-slate-500 dark:text-slate-300">
                  No relationships for admin users.
                </div>
              ) : null}
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Limits & Controls</h3>
                <p className="text-xs text-slate-500 dark:text-slate-300">Plan and policy defaults.</p>
              </div>
              {user.role === 'PARENT' ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Subscription</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    {user.parentProfile?.subscriptionType ?? 'FREE'}
                  </p>
                </div>
              ) : null}
              {user.role === 'CHILD' ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4 text-sm text-slate-500 dark:text-slate-300">
                  Child-specific limits are managed in parental controls.
                </div>
              ) : null}
              {user.role === 'ADMIN' ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/70 p-4 text-sm text-slate-500 dark:text-slate-300">
                  Admin permissions are managed by the system.
                </div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              {user.role === 'PARENT'
                ? 'Deleting this parent will also delete all active child accounts linked to them.'
                : 'This will remove access for the selected account.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl border border-rose-200/60 bg-rose-50/70 p-4 text-sm text-rose-700">
            This action is reversible by restoring the account later, but access is removed immediately.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="rounded-full bg-rose-600 text-white hover:bg-rose-500" onClick={() => handleToggleDelete(true)}>
              Delete account
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
